/**
 * CORE-E Stage B — reading and recording category background from the client.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CLIENT CANNOT SET A STRENGTH, AND THIS FILE IS WHY THAT IS STRUCTURAL
 * ═══════════════════════════════════════════════════════════════════════════
 * There is no mutation here that writes `baseline_prior_strength`, because there
 * is no RPC argument that accepts one. The client sends FACTS — years, specialty,
 * lead responsibility, a credential assertion — and the server derives the
 * strength with `core_e_derive_prior_strength`. A manager who wants a stronger
 * prior has to supply stronger facts, which is the entire design.
 *
 * `technician_category_background` is service-role write / shop-member read, so
 * even a crafted request cannot update it directly; the RPCs are the only door
 * and both gate on `is_shop_manager`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * RELEASE ORDER: MIGRATION FIRST, THIS FILE SECOND
 * ═══════════════════════════════════════════════════════════════════════════
 * This reads `technician_category_background` and calls two RPCs added by
 * `20261229120000_core_e_background_carries_the_facts.sql`. PostgREST fails the
 * whole request on an unknown table or function, so — exactly as for Stage A —
 * the migration MUST be applied before a bundle containing this becomes active.
 * No compatibility path is attempted.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** One structured rubric line the server credited. */
export interface BackgroundReason {
  code: string;
  label: string;
  points: number;
}

export interface CategoryBackgroundRow {
  categoryKey: string;
  yearsInCategory: number | null;
  isRegularSpecialty: boolean;
  leadRoleYears: number | null;
  credentialAsserted: boolean;
  historicalExposureCount: number | null;
  source: string | null;
  /** What the facts justify. Derived by the server; never set from here. */
  derivedPriorStrength: number | null;
  derivedReasons: BackgroundReason[];
  /** When the derived strength was actually applied to the authority. */
  appliedToAuthorityAt: string | null;
  appliedStrength: number | null;
  /**
   * Opaque identity of the CURRENT derivation. The apply path must name it, so
   * a screen showing an older proposal cannot apply background nobody read —
   * two different sets of facts can derive the same strength.
   */
  proposalRevision: string | null;
  /**
   * Server-owned: an unapplied proposal exists whose application would move a
   * live score. Read from the row rather than from a mutation result, so the UI
   * converges on server state and survives a reload.
   */
  pendingConfirmation: boolean;
  updatedAt: string | null;
}

/** What the writer RPC reports back. */
export interface SetBackgroundResult {
  outcome: string;
  category_key: string;
  derived_prior_strength: number;
  reasons: BackgroundReason[];
  family_state: 'ELIGIBLE' | 'INELIGIBLE' | 'UNKNOWN';
  training_backed: boolean;
  authority_exists: boolean;
  evidence_count: number;
  /** The token the apply path must name back. */
  proposal_revision: string;
  /** True when the strength was applied immediately (zero-evidence authority). */
  applied: boolean;
  /**
   * True when the authority already has evidence, so applying the derived
   * strength WOULD move a live score and therefore has not been applied. The UI
   * must say so rather than implying the edit took effect.
   */
  requires_confirmation: boolean;
}

export interface BackgroundFacts {
  yearsInCategory?: number | null;
  isRegularSpecialty?: boolean;
  leadRoleYears?: number | null;
  credentialAsserted?: boolean;
}

const KEY = (shopId?: string | null, technicianId?: string | null) =>
  ['core-e-category-background', shopId ?? null, technicianId ?? null] as const;

export function useCategoryBackground(
  shopId: string | null | undefined,
  technicianId: string | null | undefined,
) {
  return useQuery<Record<string, CategoryBackgroundRow>>({
    queryKey: KEY(shopId, technicianId),
    enabled: !!shopId && !!technicianId,
    queryFn: async () => {
      // `(supabase as any)`: Stage B is not applied to the environment the
      // generated types come from, so the union does not name this table yet —
      // the same established shape `useCategoryAuthority` uses, and the row
      // handling below coerces every field explicitly.
      const { data, error } = await (supabase as any)
        .from('technician_category_background')
        .select(
          'category_key, years_in_category, is_regular_specialty, lead_role_years, '
          + 'credential_asserted, historical_exposure_count, source, '
          + 'derived_prior_strength, derived_reasons, applied_to_authority_at, '
          + 'applied_strength, proposal_revision, pending_confirmation, updated_at',
        )
        .eq('shop_id', shopId!)
        .eq('technician_id', technicianId!);
      if (error) throw error;

      const out: Record<string, CategoryBackgroundRow> = {};
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        out[String(row.category_key)] = {
          categoryKey: String(row.category_key),
          yearsInCategory: row.years_in_category === null ? null : Number(row.years_in_category),
          isRegularSpecialty: Boolean(row.is_regular_specialty),
          leadRoleYears: row.lead_role_years === null ? null : Number(row.lead_role_years),
          credentialAsserted: Boolean(row.credential_asserted),
          historicalExposureCount:
            row.historical_exposure_count === null ? null : Number(row.historical_exposure_count),
          source: (row.source as string | null) ?? null,
          derivedPriorStrength:
            row.derived_prior_strength === null ? null : Number(row.derived_prior_strength),
          derivedReasons: Array.isArray(row.derived_reasons)
            ? (row.derived_reasons as BackgroundReason[])
            : [],
          appliedToAuthorityAt: (row.applied_to_authority_at as string | null) ?? null,
          appliedStrength: row.applied_strength === null ? null : Number(row.applied_strength),
          proposalRevision: (row.proposal_revision as string | null) ?? null,
          pendingConfirmation: Boolean(row.pending_confirmation),
          updatedAt: (row.updated_at as string | null) ?? null,
        };
      }
      return out;
    },
  });
}

/**
 * Record background facts for one category.
 *
 * Sends facts only. The result says what they were worth and — crucially —
 * whether the derived strength was applied or is waiting on a deliberate
 * confirmation because the authority already has evidence.
 */
export function useSetCategoryBackground(
  shopId: string | null | undefined,
  technicianId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation<SetBackgroundResult, Error, { categoryKey: string; facts: BackgroundFacts }>({
    mutationFn: async ({ categoryKey, facts }) => {
      const { data, error } = await (supabase as any).rpc('core_e_set_category_background', {
        p_shop_id: shopId,
        p_technician_id: technicianId,
        p_category_input: categoryKey,
        p_years_in_category: facts.yearsInCategory ?? null,
        p_is_regular_specialty: facts.isRegularSpecialty ?? false,
        p_lead_role_years: facts.leadRoleYears ?? null,
        p_credential_asserted: facts.credentialAsserted ?? false,
        // There is NO source parameter to omit any more. Omission was a
        // convention, not a boundary: any manager with EXECUTE could have
        // crafted a call naming SYSTEM_DERIVED and stored a false provenance.
        // The server writes MANAGER_ASSERTED because that is what this path IS.
      });
      if (error) throw error;
      return data as SetBackgroundResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(shopId, technicianId) });
      // The authority's strength may have changed (zero-evidence case), so the
      // Passport explanation must not keep showing the old basis.
      qc.invalidateQueries({ queryKey: ['core-e-category-authority'] });
    },
  });
}

/**
 * The deliberate second act: apply a derived strength to an authority that
 * already has evidence, knowing it will move the score.
 *
 * `expectedStrength` and `expectedRevision` are what the manager was shown. The
 * server refuses unless BOTH still match, because two different backgrounds can
 * derive the same strength — so a stale screen could otherwise apply facts
 * nobody read while the number looked right.
 */
export function useApplyCategoryBackgroundStrength(
  shopId: string | null | undefined,
  technicianId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation<
    { outcome: string; category_key: string; prior_strength: number; proposal_revision: string; score_before: number | null; score_after: number | null },
    Error,
    { categoryKey: string; expectedStrength: number; expectedRevision: string }
  >({
    mutationFn: async ({ categoryKey, expectedStrength, expectedRevision }) => {
      const { data, error } = await (supabase as any).rpc(
        'core_e_apply_category_background_strength',
        {
          p_shop_id: shopId,
          p_technician_id: technicianId,
          p_category_input: categoryKey,
          p_expected_strength: expectedStrength,
          // Confirming a VALUE is not confirming a PROPOSAL. The server refuses
          // unless both still match what the manager was shown.
          p_expected_revision: expectedRevision,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(shopId, technicianId) });
      qc.invalidateQueries({ queryKey: ['core-e-category-authority'] });
      // This one really did move the scheduling projection.
      qc.invalidateQueries({ queryKey: ['technicians'] });
    },
  });
}
